import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const BADGES = ["2 PDFs/LOT", "99%+ HPLC", "REPLACE/REFUND"];

export default function EmailDiscountPopup() {
  const { applyDiscount } = useCart();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (localStorage.getItem("pd_popup_seen")) return;
    let scrolled = false;
    const onScroll = () => {
      if (window.scrollY > 400) scrolled = true;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    const timer = setTimeout(() => {
      if (scrolled && !localStorage.getItem("pd_popup_seen")) setOpen(true);
    }, 30000);
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem("pd_popup_seen", "1");
    setOpen(false);
  };

  const reveal = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/discount/reveal", { email });
      setCode(data.code);
      // Auto-apply straight into the cart so the buyer never has to copy-paste.
      // Works even if the cart is currently empty, the discount lives in cart
      // state and will apply as soon as the first item is added.
      applyDiscount(data.code, data.discount_type, data.discount_value);
      localStorage.setItem("pd_popup_seen", "1");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" data-testid="discount-popup">
      <div className="relative grid w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl md:grid-cols-2">
        <button
          onClick={dismiss}
          className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/40 md:text-ink md:bg-black/5 md:hover:bg-black/10"
          data-testid="discount-close"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Left dark panel */}
        <div className="flex flex-col justify-between bg-navy p-8 text-white">
          <p className="font-mono text-[11px] tracking-[0.2em] text-white/70">■ INTRODUCTORY OFFER · ONE TIME</p>
          <div className="py-8">
            <div className="font-heading text-7xl font-black leading-none">10%</div>
            <div className="mt-1 font-heading text-2xl font-bold text-brandblue">% OFF</div>
            <p className="mt-3 text-lg text-white/80">Off your first order.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {["LOCKED TO EMAIL"].map((t) => (
              <span key={t} className="rounded-sm bg-white/10 px-2 py-1 font-mono text-[10px] tracking-[0.12em] text-white/80">
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* Right white panel */}
        <div className="p-8">
          {!code ? (
            <>
              <p className="label-mono">STEP 01 / 01 · 20 SECONDS</p>
              <h3 className="mt-2 font-heading text-2xl font-black text-ink">Reveal your code.</h3>
              <p className="mt-2 text-sm text-subtle">
                A unique discount code, locked to your email. Single use, 30 days.
              </p>
              <form onSubmit={reveal} className="mt-5">
                <label className="label-mono">Researcher email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="dr.you@research.org"
                  className="mt-2 w-full rounded-md border border-line px-4 py-3 text-sm outline-none focus:border-brandblue focus:ring-2 focus:ring-brandblue/20"
                  data-testid="discount-email-input"
                />
                {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-pill mt-4 w-full bg-navy py-3 text-sm font-semibold text-white hover:bg-navydark disabled:opacity-60"
                  data-testid="discount-reveal-button"
                >
                  {loading ? "Revealing..." : "Reveal my 10% code →"}
                </button>
              </form>
              <p className="mt-3 text-[11px] text-subtle">
                Single use · 30 days · 1 lot-release alert per month max · unsubscribe in one click
              </p>
            </>
          ) : (
            <div className="flex h-full flex-col justify-center text-center">
              <p className="label-mono">YOUR CODE</p>
              <div className="mt-3 rounded-lg border-2 border-dashed border-brandblue bg-brandblue/5 py-5 font-heading text-3xl font-black tracking-wider text-navy" data-testid="discount-code-result">
                {code}
              </div>
              <p className="mt-3 text-sm text-subtle">Locked to {email}. Auto-applied to your cart, proceed to checkout when ready.</p>
              <button onClick={dismiss} className="btn-pill mt-5 bg-navy py-3 text-sm font-semibold text-white hover:bg-navydark">
                Start browsing →
              </button>
            </div>
          )}
          <div className="mt-6 grid grid-cols-3 gap-2 border-t border-line pt-5">
            {BADGES.map((b) => (
              <span key={b} className="text-center font-mono text-[9px] tracking-[0.1em] text-subtle">{b}</span>
            ))}
          </div>
          <button onClick={dismiss} className="mt-4 w-full text-center font-mono text-[10px] tracking-[0.15em] text-subtle hover:text-ink" data-testid="discount-dismiss">
            NO THANKS, I'LL PAY FULL PRICE
          </button>
        </div>
      </div>
    </div>
  );
}
